import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Button,
  InputAdornment,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import processService from '../services/processService';
import { STATUS_CONFIG, getStatusConfig } from '../config/statusConfig';

const ProcessSettings = () => {
  const navigate = useNavigate();
  
  // State for data
  const [processes, setProcesses] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filters and pagination
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);

  // Load data on initialization
  useEffect(() => {
    fetchProcesses();
    fetchProcessTypes();
  }, []);

  const fetchProcessTypes = async () => {
    try {
      const response = await processService.getAllProcessesTypes();
      if (response && Array.isArray(response)) {
        setProcessTypes(response);
      }
    } catch (err) {
      console.error('Error fetching process types:', err);
    }
  };

  const fetchProcesses = async () => {
    try {
      setLoading(true);
      const response = await processService.getAll();
      if (response && Array.isArray(response)) {
        setProcesses(response);
        setError(null);
      } else {
        setProcesses([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Error fetching processes:', err);
      setError('Failed to load processes data. Please try again later.');
      setProcesses([]);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateProcess = () => {
    navigate('/new-process');
  };

  const handleEditProcess = (process) => {
    navigate(`/processes/${process.id}`);
  };

  const handleDeleteProcess = (process) => {
    setProcessToDelete(process);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await processService.delete(processToDelete.id);
      setProcesses(processes.filter(p => p.id !== processToDelete.id));
      setDeleteDialogOpen(false);
      setProcessToDelete(null);
    } catch (error) {
      console.error('Error deleting process:', error);
      setError('Failed to delete process');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  // Filter data based on selected filters
  const filteredProcesses = React.useMemo(() => {
    return processes.filter(process => {
      const matchesSearch = !searchQuery || 
        (process?.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (process?.code?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (process?.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      
      const matchesTypeFilter = !typeFilter || process?.processType?.id === typeFilter;
      
      return matchesSearch && matchesTypeFilter;
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [processes, searchQuery, typeFilter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestione Processi</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl size="small" sx={{ ml: 1, minWidth: 120 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Tipo"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {processTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Chip 
                          label={type.id} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleCreateProcess}
                >
                  Aggiungi
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Versione</TableCell>
                <TableCell>Tipo Processo</TableCell>
                <TableCell>Data Creazione</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProcesses.length > 0 ? (
                filteredProcesses
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((process) => {
                    const statusConfig = getStatusConfig('PROCESS', process.status);
                    return (
                      <TableRow key={process.id}>
                        <TableCell>{process.code || '-'}</TableCell>
                        <TableCell>{process.description || '-'}</TableCell>
                        <TableCell>{process.version || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={process.processTypeId || '-'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(process.createdAt)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton 
                              onClick={() => handleEditProcess(process)}
                              size="small"
                              title="Modifica processo"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteProcess(process)}
                              size="small"
                              color="error"
                              title="Elimina processo"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nessun processo trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredProcesses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il processo {processToDelete?.processCode}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessSettings; 